import { useEffect, useRef, useCallback } from "react";

const WATCHED_EVENTS = ["mousemove", "keydown", "scroll", "click", "touchstart"];

/**
 * Detects user inactivity while a timer is running.
 *
 * @param {object} options
 * @param {boolean} options.enabled - Only watch when true (i.e. timer is running)
 * @param {number} options.thresholdMs - Idle threshold in ms (default: 30 minutes)
 * @param {number} options.checkIntervalMs - How often to check (default: 60 seconds)
 * @param {function} options.onIdle - Called with (idleMinutes) when threshold exceeded
 *
 * @returns {{ resetIdle: function }} - Call resetIdle() to manually reset the idle clock
 */
export function useIdleDetection({
  enabled = false,
  thresholdMs = 30 * 60 * 1000,
  checkIntervalMs = 60 * 1000,
  onIdle,
}) {
  const lastActivityRef = useRef(Date.now());
  const firedRef = useRef(false); // prevent repeated firing until the user acts again

  const resetIdle = useCallback(() => {
    lastActivityRef.current = Date.now();
    firedRef.current = false;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const handleActivity = () => resetIdle();

    WATCHED_EVENTS.forEach((e) =>
      window.addEventListener(e, handleActivity, { passive: true })
    );

    const intervalId = setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      if (!firedRef.current && idleMs >= thresholdMs) {
        firedRef.current = true;
        onIdle(Math.floor(idleMs / 60_000));
      }
    }, checkIntervalMs);

    return () => {
      WATCHED_EVENTS.forEach((e) =>
        window.removeEventListener(e, handleActivity)
      );
      clearInterval(intervalId);
    };
  }, [enabled, thresholdMs, checkIntervalMs, onIdle, resetIdle]);

  return { resetIdle };
}
